import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  checkNumber, getCallSettings, getCallStatus, setDeskPhone, startDeskCall, startNumberCheck,
} from '../../../api/localIntelligence';
import { BTN, BTN_PRIMARY, EYEBROW } from '../liStyles';

// Calling a place from the desk. The number is always shown big so it can be dialed by
// hand. When the park has desk calling, "Ring my desk phone" makes Twilio ring the desk
// phone first and connect it to the place once someone picks up; the call's progress is
// read back from the database. Every label is the database's or the edge function's.
const INPUT = 'h-9 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400';
const POLL_MS = 1500;
const CALL_WATCH_MS = 3 * 60 * 1000;
const VERIFY_WATCH_MS = 2 * 60 * 1000;

const CallPanel = ({ clientId, place, onOutcome }) => {
  const [settings, setSettings] = useState({ loading: true, data: null, error: null });
  const [editingDesk, setEditingDesk] = useState(false);
  const [deskInput, setDeskInput] = useState('');
  const [deskSave, setDeskSave] = useState({ saving: false, error: null });
  const [call, setCall] = useState(null);
  const [verify, setVerify] = useState(null);
  const timers = useRef([]);

  const later = (fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); };
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const load = useCallback(() => {
    setSettings((s) => ({ ...s, loading: true, error: null }));
    getCallSettings(clientId)
      .then((data) => setSettings({ loading: false, data, error: null }))
      .catch((e) => setSettings({ loading: false, data: null, error: e?.message || 'Could not load desk calling.' }));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const s = settings.data;
  const showDeskForm = Boolean(s && s.caller_id && (!s.desk || editingDesk));

  const saveDesk = async (e) => {
    e.preventDefault();
    if (!deskInput.trim()) return;
    setDeskSave({ saving: true, error: null });
    try {
      const data = await setDeskPhone(clientId, deskInput.trim());
      setSettings({ loading: false, data, error: null });
      setEditingDesk(false);
      setDeskSave({ saving: false, error: null });
    } catch (err) {
      setDeskSave({ saving: false, error: err?.message || 'Could not save the desk phone.' });
    }
  };

  const watchCall = (callId, startedAt) => {
    getCallStatus(clientId, callId)
      .then((r) => {
        setCall((c) => (c && c.id === callId ? { ...c, label: r?.label || c.label, done: Boolean(r?.done) } : c));
        if (!r?.done && Date.now() - startedAt < CALL_WATCH_MS) later(() => watchCall(callId, startedAt), POLL_MS);
      })
      .catch(() => { if (Date.now() - startedAt < CALL_WATCH_MS) later(() => watchCall(callId, startedAt), POLL_MS * 2); });
  };

  const ring = async () => {
    setCall({ id: null, label: 'Calling your desk phone…', done: false, error: null, starting: true });
    try {
      const r = await startDeskCall(clientId, place.entity_id);
      setCall({ id: r.call_id, label: 'Calling your desk phone…', done: false, error: null });
      if (onOutcome && place.recommendation_id) onOutcome(place.recommendation_id, 'called');
      later(() => watchCall(r.call_id, Date.now()), POLL_MS);
    } catch (err) {
      setCall({ id: null, label: null, done: true, error: err?.message || 'The call could not be placed.' });
    }
  };

  const watchVerify = (startedAt) => {
    getCallSettings(clientId)
      .then((data) => {
        if (data?.public_phone?.verified) {
          setSettings({ loading: false, data, error: null });
          setVerify({ done: true, display: data.public_phone.display });
        } else if (Date.now() - startedAt < VERIFY_WATCH_MS) {
          later(() => watchVerify(startedAt), 3000);
        }
      })
      .catch(() => {});
  };

  const startVerify = async () => {
    setVerify({ starting: true });
    try {
      const r = await startNumberCheck(clientId);
      if (r.already_verified) { load(); setVerify({ done: true, display: r.display }); return; }
      setVerify({ code: r.code, display: r.display });
      later(() => watchVerify(Date.now()), 3000);
    } catch (err) {
      setVerify({ error: err?.message || 'Could not start the check.' });
    }
  };

  const recheck = async () => {
    try {
      const r = await checkNumber(clientId);
      if (r.verified) { load(); setVerify({ done: true, display: r.display }); }
      else setVerify((v) => ({ ...v, note: 'Not confirmed yet. Answer the call from Twilio and enter the code.' }));
    } catch (err) {
      setVerify((v) => ({ ...v, note: err?.message || 'Could not check.' }));
    }
  };

  const canVerify = s?.public_phone && !s.public_phone.verified && s.caller_id && s.caller_id.kind !== 'park_verified';

  return (
    <div className="space-y-6" data-testid="li-call-panel">
      <div className="space-y-1">
        <div className={EYEBROW}>{place.name}</div>
        <div className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100" data-testid="li-call-number">
          {place.phone || 'No phone number on file'}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">Dial it from the desk phone, or let the system ring your desk phone and connect you.</p>
      </div>

      {settings.loading && <p className="text-sm text-gray-500 dark:text-gray-400">Checking desk calling…</p>}
      {settings.error && <p className="text-sm text-red-600 dark:text-red-400">{settings.error}</p>}

      {s && !s.caller_id && s.setup_label && (
        <p className="text-sm text-gray-600 dark:text-gray-400">{s.setup_label}</p>
      )}

      {showDeskForm && (
        <form onSubmit={saveDesk} className="space-y-2" data-testid="li-desk-form">
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100" htmlFor="li-desk-phone">
            The phone at your desk
          </label>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {s.setup_label || 'Call rings this phone first, then connects you.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              id="li-desk-phone"
              className={`${INPUT} w-48`}
              inputMode="tel"
              placeholder="(555) 555-0100"
              value={deskInput}
              onChange={(e) => setDeskInput(e.target.value)}
            />
            <button type="submit" className={BTN_PRIMARY} disabled={deskSave.saving || !deskInput.trim()}>
              {deskSave.saving ? 'Saving…' : 'Save'}
            </button>
            {editingDesk && <button type="button" className={BTN} onClick={() => setEditingDesk(false)}>Cancel</button>}
          </div>
          {deskSave.error && <p className="text-sm text-red-600 dark:text-red-400">{deskSave.error}</p>}
        </form>
      )}

      {s && s.caller_id && s.desk && !editingDesk && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={BTN_PRIMARY} onClick={ring} disabled={!place.phone || Boolean(call && !call.done)}>
              Ring my desk phone {s.desk.display}
            </button>
            <button type="button" className={BTN} onClick={() => { setDeskInput(s.desk.display || ''); setEditingDesk(true); }}>
              Change desk phone
            </button>
          </div>
          {call && (call.label || call.error) && (
            <p className={`text-sm ${call.error ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'}`} aria-live="polite" data-testid="li-call-status">
              {call.error || call.label}
            </p>
          )}
        </div>
      )}

      {s && s.caller_id && (
        <div className="border-t border-gray-100 dark:border-gray-800 pt-4 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>{place.name} will see {s.caller_id.display}, {s.caller_id.label}.</p>
          {canVerify && !verify && (
            <p>
              Show {s.public_phone.display} instead?{' '}
              <button type="button" className="text-[#32B1FF] hover:underline font-medium" onClick={startVerify}>Verify your number</button>
            </p>
          )}
          {verify?.starting && <p>Starting the check…</p>}
          {verify?.code && !verify.done && (
            <div className="space-y-2" data-testid="li-verify">
              <p className="text-gray-800 dark:text-gray-200">
                Twilio is calling {verify.display} now. Answer it and enter this code:
              </p>
              <div className="text-2xl font-semibold tracking-widest text-gray-900 dark:text-gray-100">{verify.code}</div>
              <button type="button" className={BTN} onClick={recheck}>I entered it</button>
              {verify.note && <p>{verify.note}</p>}
            </div>
          )}
          {verify?.done && <p className="text-emerald-700 dark:text-emerald-400">Verified. Calls now show {verify.display}.</p>}
          {verify?.error && <p className="text-red-600 dark:text-red-400">{verify.error}</p>}
        </div>
      )}
    </div>
  );
};

export default CallPanel;
